/**
 * Structured types for proposal sections
 *
 * These types define the JSON structure that the AI returns for each section.
 * The frontend renders each field with dedicated UI components instead of parsing markdown.
 */

// ============================================================================
// COVER LETTER
// ============================================================================

export interface CoverLetterContent {
  companyInfo: {
    name: string;
    address: string;
    cityStateZip: string;
    phone: string;
    email: string;
    website?: string;
  };
  date: string;
  recipient: {
    agency: string;
    title: string;
    address?: string;
    name?: string | null;
    office?: string | null;
    salutation?: string;
  };
  subject: {
    contractTitle: string;
    solicitationNumber?: string;
    naicsCode?: string;
  };
  body: {
    opening: string;
    qualifications: string;
    valueProposition: string;
    understanding: string;
    closing: string;
  };
  signature: {
    salutation: string;
    name: string;
    title: string;
    company: string;
  };
  enclosures: string[];
}

// ============================================================================
// EXECUTIVE SUMMARY
// ============================================================================

export interface ExecutiveSummaryContent {
  valueProposition: string;
  understandingOfRequirements: string;
  keyDiscriminators: Array<{
    title: string;
    description: string;
  }>;
  relevantExperience: Array<{
    project: string;
    client: string;
    value: string;
    relevance: string;
  }>;
  technicalOverview: string;
  managementOverview: string;
  conclusion: string;
}

// ============================================================================
// TECHNICAL APPROACH
// ============================================================================

export interface TechnicalApproachContent {
  scopeOverview: string;
  technicalChallenges: string;
  methodology: {
    overallApproach: string;
    tasks: Array<{
      name: string;
      approach: string;
      tools: string[];
      deliverables: string[];
    }>;
  };
  toolsAndTechnologies: Array<{
    category: string;
    technology: string;
    purpose: string;
    evidence?: string;
  }>;
  innovation: string;
  qualityAssurance: string;
  schedule: Array<{
    phase: string;
    duration: string;
    milestones: string;
    deliverables: string;
  }>;
}

// ============================================================================
// MANAGEMENT APPROACH
// ============================================================================

export interface ManagementApproachContent {
  corporateOrganization: string;
  organizationChart: string; // Text representation of org chart
  keyRoles: Array<{
    role: string;
    name?: string;
    responsibilities: string[];
    qualifications: string;
    evidence?: string;
  }>;
  communicationPlan: {
    reportingStructure: string;
    meetings: Array<{
      type: string;
      frequency: string;
      attendees: string;
      purpose: string;
    }>;
  };
  qualityManagement: {
    process: string;
    metrics: Array<{
      metric: string;
      target: string;
      method: string;
    }>;
  };
  riskManagement: Array<{
    risk: string;
    likelihood: string;
    impact: string;
    mitigation: string;
  }>;
  transitionPlan: string;
}

// ============================================================================
// PAST PERFORMANCE
// ============================================================================

export interface PastPerformanceContent {
  summary: string;
  contracts: Array<{
    name: string;
    contractNumber?: string;
    client: string;
    value: string;
    period: string;
    contractType?: string;
    scopeOfWork: string;
    relevance: string;
    achievements: string[];
    reference?: {
      name: string;
      title: string;
      phone?: string;
      email?: string;
    };
    evidence?: string;
  }>;
  relevanceSummary: string;
}

// ============================================================================
// PERSONNEL
// ============================================================================

export interface PersonnelContent {
  summary: string;
  keyPersonnel: Array<{
    role: string;
    name: string;
    yearsExperience: number;
    clearance?: string;
    certifications: string[];
    education: string[];
    relevantExperience: Array<{
      position: string;
      organization: string;
      years: string;
      responsibilities: string[];
    }>;
    keyProjects?: string[];
    evidence?: string;
  }>;
  staffingPlan: Array<{
    category: string;
    quantity: number;
    fte: string;
    location: string;
  }>;
  recruitmentApproach: string;
  substitutionProcess: string;
}

// ============================================================================
// PRICING
// ============================================================================

export interface PricingContent {
  contractTypeRecommendation: {
    recommended: string;
    rationale: string;
  };
  laborCategories: Array<{
    category: string;
    equivalent: string;
    minExperience: string;
    education: string;
    hourlyRate: string;
    loadedRate: string;
  }>;
  rateMethodology: string;
  indirectRates: Array<{
    category: string;
    rate: string;
    basis: string;
    auditStatus: string;
  }>;
  costBreakdown: {
    headers: string[];
    rows: Array<{
      element: string;
      values: string[];
    }>;
  };
  otherDirectCosts: Array<{
    category: string;
    description: string;
    quantity: string;
    unitCost: string;
    total: string;
  }>;
  basisOfEstimate: string;
  priceRealism: string;
  valueProposition: {
    efficiencyMeasures: string[];
    bestValueDetermination: string;
  };
}

// ============================================================================
// COMPLIANCE
// ============================================================================

export interface ComplianceContent {
  farCompliance: Array<{
    clause: string;
    title: string;
    status: 'compliant' | 'partial' | 'gap';
    notes: string;
  }>;
  dfarsCompliance?: Array<{
    clause: string;
    title: string;
    status: 'compliant' | 'partial' | 'gap';
    notes: string;
  }>;
  certifications: Array<{
    name: string;
    status: string;
    number?: string;
    expiration?: string;
  }>;
  securityClearances?: Array<{
    type: string;
    level: string;
    status: string;
    agency?: string;
  }>;
  acknowledgments: string;
}

// ============================================================================
// UNIFIED SECTION TYPE
// ============================================================================

export type ProposalSectionContent =
  | { type: 'coverLetter'; data: CoverLetterContent }
  | { type: 'executiveSummary'; data: ExecutiveSummaryContent }
  | { type: 'technical'; data: TechnicalApproachContent }
  | { type: 'management'; data: ManagementApproachContent }
  | { type: 'pastPerformance'; data: PastPerformanceContent }
  | { type: 'personnel'; data: PersonnelContent }
  | { type: 'pricing'; data: PricingContent }
  | { type: 'compliance'; data: ComplianceContent };

// ============================================================================
// SECTION WITH METADATA
// ============================================================================

export interface StructuredSection<T> {
  sectionType: string;
  title: string;
  content: T;
  contentWithCitations?: string; // Legacy: raw text with citations for reference
  usedEvidence: string[];
  gaps: string[];
  confidence: number;
  wordCount: number;
}

// Type guards for section content
export function isCoverLetterContent(content: unknown): content is CoverLetterContent {
  return typeof content === 'object' && content !== null && 'companyInfo' in content && 'body' in content;
}

export function isExecutiveSummaryContent(content: unknown): content is ExecutiveSummaryContent {
  return typeof content === 'object' && content !== null && 'valueProposition' in content && 'keyDiscriminators' in content;
}

export function isTechnicalApproachContent(content: unknown): content is TechnicalApproachContent {
  return typeof content === 'object' && content !== null && 'methodology' in content && 'toolsAndTechnologies' in content;
}

export function isManagementApproachContent(content: unknown): content is ManagementApproachContent {
  return typeof content === 'object' && content !== null && 'keyRoles' in content && 'communicationPlan' in content;
}

export function isPastPerformanceContent(content: unknown): content is PastPerformanceContent {
  return typeof content === 'object' && content !== null && 'contracts' in content && Array.isArray((content as any).contracts);
}

export function isPersonnelContent(content: unknown): content is PersonnelContent {
  return typeof content === 'object' && content !== null && 'keyPersonnel' in content && Array.isArray((content as any).keyPersonnel);
}

export function isPricingContent(content: unknown): content is PricingContent {
  if (typeof content !== 'object' || content === null) return false;
  const c = content as Record<string, unknown>;
  // FAR-15 cost-buildup output carries laborCategories + costBreakdown. Commercial-ffp /
  // tm-lh DE-LEAK those (no disclosed indirect rates or grand total) and are identified by
  // their pricing mode, CLIN price schedule, or cover page instead. Accept any of these
  // shapes so commercial pricing still routes to PricingRenderer — otherwise it fell
  // through to a raw-JSON dump after the Phase-1 de-leak.
  return (
    ('laborCategories' in c && 'costBreakdown' in c) ||
    'pricingMode' in c ||
    'clinPriceSchedule' in c ||
    'farTable15_2CoverPage' in c
  );
}

export function isComplianceContent(content: unknown): content is ComplianceContent {
  return typeof content === 'object' && content !== null && 'farCompliance' in content;
}
